import type { UrlMatchResult, UrlSegment } from '@angular/router';

/**
 * Todas las URL del curso del estudiante, en un solo lugar.
 *
 *   /estudiante/curso/:cursoId                       portada de la asignatura
 *   /estudiante/curso/:cursoId/unidad/:unidadId      una unidad (o un hito)
 *   /estudiante/curso/:cursoId/episodio/:temaId      un tema
 *   /estudiante/curso/:cursoId/proyecto              el documento del proyecto de grado
 *   ...?vista=progreso                               pestaña abierta, opcional
 *   /estudiante/curso/:cursoId/bienvenida            portal de entrada a clase
 *
 * Quien necesite llevar al estudiante a algún punto del curso construye la
 * ruta aquí en vez de armar el arreglo a mano, para que un cambio de URL se
 * haga una sola vez y siga funcionando con los enlaces ya guardados.
 */

export const RAIZ_CURSO = '/estudiante/curso';

export type RutaCurso = (string | number)[];

// --------------------------------------------------------------- pestañas

export type VistaUnidad = 'ruta' | 'progreso' | 'info';
export type VistaTema = 'contenido' | 'progreso' | 'actividades';

export const VISTA_UNIDAD_INICIAL: VistaUnidad = 'ruta';
export const VISTA_TEMA_INICIAL: VistaTema = 'contenido';

const VISTAS_UNIDAD: readonly VistaUnidad[] = ['ruta', 'progreso', 'info'];
const VISTAS_TEMA: readonly VistaTema[] = ['contenido', 'progreso', 'actividades'];

/** La pestaña pedida por la URL, o la inicial si el valor no existe. */
export function vistaDeUnidad(valor: string | null): VistaUnidad {
  return VISTAS_UNIDAD.find(v => v === valor) ?? VISTA_UNIDAD_INICIAL;
}

export function vistaDeTema(valor: string | null): VistaTema {
  return VISTAS_TEMA.find(v => v === valor) ?? VISTA_TEMA_INICIAL;
}

/**
 * Parámetros de consulta para una pestaña. La inicial no se escribe: así la URL
 * limpia sigue siendo la de siempre y solo se ensucia cuando hay algo que decir.
 */
export function consultaVista(vista: string, inicial: string): { vista: string | null } {
  return { vista: vista === inicial ? null : vista };
}

// ------------------------------------------------------------------ rutas

export function rutaPortada(cursoId: string): RutaCurso {
  return [RAIZ_CURSO, cursoId];
}

export function rutaUnidad(cursoId: string, unidadId: number | string): RutaCurso {
  return [RAIZ_CURSO, cursoId, 'unidad', unidadId];
}

export function rutaTema(cursoId: string, temaId: number | string): RutaCurso {
  return [RAIZ_CURSO, cursoId, 'episodio', temaId];
}

/** El documento del proyecto de grado: la portada de una asignatura con vista de monografía lleva aquí. */
export function rutaProyecto(cursoId: string): RutaCurso {
  return [RAIZ_CURSO, cursoId, 'proyecto'];
}

export function rutaBienvenida(cursoId: string): RutaCurso {
  return [RAIZ_CURSO, cursoId, 'bienvenida'];
}

// ------------------------------------------------------- reconocer la URL

/**
 * Reconoce `curso/:cursoId/unidad/:unidadId` y `curso/:cursoId/episodio/:temaId`
 * como UNA sola ruta. Con dos rutas distintas el componente se destruía y se
 * volvía a crear (y a cargar el curso entero) al pasar de una unidad a un tema;
 * con una sola, solo cambia el parámetro y el curso ya cargado se reaprovecha.
 */
export function cursoMatcher(segmentos: UrlSegment[]): UrlMatchResult | null {
  if (segmentos.length === 3 && segmentos[0].path === 'curso' && segmentos[2].path === 'proyecto') {
    return { consumed: segmentos, posParams: { cursoId: segmentos[1] } };
  }

  if (segmentos.length !== 4 || segmentos[0].path !== 'curso') return null;

  const tipo = segmentos[2].path;
  if (tipo !== 'unidad' && tipo !== 'episodio') return null;

  return {
    consumed: segmentos,
    posParams: {
      cursoId: segmentos[1],
      [tipo === 'unidad' ? 'unidadId' : 'temaId']: segmentos[3]
    }
  };
}

/** Un id numérico válido de la URL, o null si falta o viene mal escrito. */
export function idDeUrl(valor: string | null): number | null {
  if (valor === null) return null;
  const n = Number(valor);
  return Number.isInteger(n) && n > 0 ? n : null;
}
